import React, { Fragment, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import IntegrationHero from '../../components/integration-hero'
import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import { getCanonicalUrl } from '../../utils/canonical'
import DarkNav from '../../components/dark-nav'
import './supabase.css'

const SupabaseIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    // Initialize AOS animations if available
    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }

    // Scroll to top functionality
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)

      if (typeof AOS !== 'undefined') {
        AOS.refresh()
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const trackEvent = (eventName, properties = {}) => {
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, properties)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'Supabase' }
  ]

  const heroContent = {
    title: "Supabase SMS Integration",
    subtitle: "Send OTP & SMS from Supabase Edge Functions",
    description: "Add SMS authentication to your Supabase project with our open-source Edge Function. Send OTP verification codes, enable phone-based login, and replace expensive providers like Twilio with cost-effective Australian SMS delivery.",
    primaryCta: {
      text: "View on GitHub",
      href: "https://github.com/dataflows-au/supabase-sms-hook",
      onClick: () => trackEvent('supabase_github_clicked')
    },
    secondaryCta: {
      text: "Get API Key",
      href: "https://sms.dataflows.com.au/register"
    }
  }

  const features = [
    {
      icon: "🔐",
      title: "OTP Phone Authentication",
      description: "Enable secure phone number verification for your Supabase Auth users. Send one-time passwords (OTP) via SMS for user registration, login, and two-factor authentication with industry-standard security."
    },
    {
      icon: "⚡",
      title: "Edge Function Deployment",
      description: "Deploy directly to Supabase Edge Functions with zero configuration complexity. Our TypeScript-based hook integrates seamlessly with Supabase's serverless infrastructure for low-latency SMS delivery worldwide."
    },
    {
      icon: "🔒",
      title: "Webhook Security",
      description: "Built-in webhook signature validation ensures only legitimate requests from your Supabase project trigger SMS sends. Protect against unauthorized API usage with cryptographic verification."
    },
    {
      icon: "💰",
      title: "Cost-Effective Alternative",
      description: "Replace expensive SMS providers like Twilio with DataFlows' competitive pricing. Send Australian SMS from just 3.5¢ per message with no monthly fees, contracts, or hidden charges."
    },
    {
      icon: "🌏",
      title: "Australian & International",
      description: "Optimized for Australian businesses with local number support, plus international SMS delivery to 200+ countries. Perfect for APAC-focused applications requiring reliable SMS infrastructure."
    },
    {
      icon: "📝",
      title: "Customizable Templates",
      description: "Personalize your OTP messages with custom templates. Configure sender IDs, message content, and branding to match your application's identity and user experience requirements."
    }
  ]

  const useCases = [
    {
      icon: "📱",
      title: "Phone Number Login",
      description: "Allow users to sign in with their phone number instead of email. Perfect for mobile-first applications where phone verification provides better user experience and higher conversion rates.",
      keywords: ["Supabase phone login", "passwordless authentication", "mobile sign-in"]
    },
    {
      icon: "✅",
      title: "User Verification",
      description: "Verify user phone numbers during registration to reduce fake accounts and improve security. Essential for marketplaces, financial apps, and platforms requiring identity verification.",
      keywords: ["phone verification", "user validation", "identity confirmation"]
    },
    {
      icon: "🛡️",
      title: "Two-Factor Authentication",
      description: "Add an extra layer of security with SMS-based 2FA. Protect user accounts from unauthorized access by requiring both password and OTP verification for sensitive operations.",
      keywords: ["2FA", "two-factor auth", "SMS security"]
    },
    {
      icon: "🔔",
      title: "Transactional Notifications",
      description: "Extend beyond authentication to send order confirmations, appointment reminders, delivery updates, and other critical notifications directly from your Supabase backend.",
      keywords: ["SMS notifications", "transactional SMS", "alerts"]
    }
  ]

  const setupSteps = [
    {
      step: 1,
      title: "Get DataFlows Credentials",
      description: "Sign up for a DataFlows account and retrieve your API Token and Sender ID from the Developer Settings page.",
      code: null
    },
    {
      step: 2,
      title: "Clone the Edge Function",
      description: "Copy the Edge Function code from our GitHub repository to your Supabase project's functions directory.",
      code: `supabase functions new send-sms-hook
# Copy the function code from GitHub`
    },
    {
      step: 3,
      title: "Configure Secrets",
      description: "Set up the required environment variables for secure API communication with DataFlows.",
      code: `supabase secrets set DATAFLOWS_API_KEY=your_api_key
supabase secrets set DATAFLOWS_SENDER_ID=your_sender_id
supabase secrets set SEND_SMS_HOOK_SECRET=your_webhook_secret`
    },
    {
      step: 4,
      title: "Deploy the Function",
      description: "Deploy your Edge Function to Supabase's global edge network for low-latency execution.",
      code: `supabase functions deploy send-sms-hook`
    },
    {
      step: 5,
      title: "Configure Auth Hook",
      description: "In your Supabase Dashboard, navigate to Authentication → Hooks → Send SMS and register your function URL.",
      code: null
    },
    {
      step: 6,
      title: "Enable Phone Provider",
      description: "Go to Authentication → Providers → Phone and select 'Hook' as the SMS delivery method. Generate and save the webhook secret.",
      code: null
    }
  ]

  const codeExamples = {
    requestOtp: `// Request OTP for phone authentication
const { data, error } = await supabase.auth.signInWithOtp({
  phone: '+61412345678'
})

if (error) {
  console.error('Error sending OTP:', error.message)
} else {
  console.log('OTP sent successfully!')
}`,
    verifyOtp: `// Verify the OTP code
const { data, error } = await supabase.auth.verifyOtp({
  phone: '+61412345678',
  token: '123456',
  type: 'sms'
})

if (error) {
  console.error('Verification failed:', error.message)
} else {
  console.log('User verified:', data.user)
}`,
    customTemplate: `// Environment variable for custom OTP template
OTP_MESSAGE_TEMPLATE="Your {{app_name}} code is: {{otp}}"

// Default template if not specified
"Your verification code is: {otp}"`
  }

  const benefits = [
    "Open-source and fully customizable — modify the code to fit your exact requirements",
    "No vendor lock-in — switch providers or self-host without rewriting your authentication flow",
    "Australian data sovereignty — messages processed through Australian infrastructure",
    "Instant activation — no lengthy approval processes or account verification delays",
    "Prepaid credits — only pay for what you use with no monthly minimums"
  ]

  const technicalSpecs = [
    { label: "Runtime", value: "Deno (Supabase Edge Functions)" },
    { label: "Language", value: "TypeScript" },
    { label: "License", value: "MIT (Open Source)" },
    { label: "Auth Methods", value: "OTP, Phone Login, 2FA" },
    { label: "API Format", value: "REST / JSON" },
    { label: "Webhook Security", value: "HMAC-SHA256 Signature" }
  ]

  const faqItems = [
    {
      question: "How does the Supabase SMS Hook work?",
      answer: "When a user requests phone authentication in your Supabase app, Supabase Auth triggers the Edge Function webhook. The function receives the phone number and OTP, validates the webhook signature for security, then sends the SMS via DataFlows API. The entire process happens in milliseconds."
    },
    {
      question: "Is this a replacement for Twilio in Supabase?",
      answer: "Yes! This integration serves as a cost-effective alternative to Twilio for Supabase SMS authentication. You get the same functionality—OTP delivery, phone verification, and SMS authentication—at a fraction of the cost, especially for Australian traffic."
    },
    {
      question: "Can I use this for non-authentication SMS?",
      answer: "Absolutely. While the primary use case is Supabase Auth OTP delivery, you can call the DataFlows API directly from any Edge Function or server-side code to send transactional SMS, notifications, alerts, and marketing messages."
    },
    {
      question: "What phone number formats are supported?",
      answer: "The integration supports E.164 international format (e.g., +61412345678 for Australian numbers). All major country codes are supported, with optimized routing for Australian, New Zealand, and APAC destinations."
    },
    {
      question: "How do I customize the OTP message template?",
      answer: "Set the OTP_MESSAGE_TEMPLATE environment variable with your custom message. Use {otp} as a placeholder for the verification code. For example: 'Your AppName verification code is: {otp}. Valid for 10 minutes.'"
    },
    {
      question: "Is the webhook secure?",
      answer: "Yes. The Edge Function validates every incoming request using HMAC-SHA256 signature verification. Only requests signed with your Supabase webhook secret are processed, preventing unauthorized SMS sends."
    },
    {
      question: "What happens if SMS delivery fails?",
      answer: "The Edge Function returns appropriate error responses to Supabase Auth, which handles retry logic. DataFlows provides delivery status callbacks and dashboard visibility so you can monitor success rates and troubleshoot issues."
    },
    {
      question: "Can I test locally before deploying?",
      answer: "Yes! Use the Supabase CLI to run Edge Functions locally with environment variables in a .env file. This allows you to test the integration without affecting production users."
    }
  ]

  const comparisonData = [
    { feature: "Australian SMS", dataflows: "3.5¢", twilio: "7.5¢+", savings: "53%" },
    { feature: "Monthly Fee", dataflows: "None", twilio: "$0-15+", savings: "100%" },
    { feature: "Inbound SMS", dataflows: "Free", twilio: "0.75¢+", savings: "100%" },
    { feature: "Setup Time", dataflows: "Minutes", twilio: "Hours", savings: "-" },
    { feature: "Contract Required", dataflows: "No", twilio: "Sometimes", savings: "-" }
  ]

  return (
    <div className="supabase-integration-container">
      <DarkNav />
      <Helmet>
        <title>Supabase SMS Integration — Send OTP & Phone Authentication | DataFlows</title>
        <meta
          name="description"
          content="Add SMS authentication to Supabase with our open-source Edge Function. Send OTP codes, enable phone login, and replace Twilio with cost-effective Australian SMS. Free GitHub repo included."
        />
        <meta name="keywords" content="Supabase SMS, Supabase OTP, Supabase phone authentication, Supabase SMS verification, Supabase Edge Functions SMS, Supabase Auth SMS, Supabase Twilio alternative, send SMS from Supabase, Supabase phone login, Supabase SMS hook, Supabase SMS provider, Supabase authentication, phone verification Supabase, Supabase 2FA, two-factor authentication Supabase, Supabase Australia SMS, Supabase signInWithOtp, Supabase verifyOtp, passwordless login Supabase" />
        <meta property="og:title" content="Supabase SMS Integration — OTP & Phone Authentication" />
        <meta property="og:description" content="Open-source Supabase Edge Function for SMS authentication. Send OTP codes and enable phone login with DataFlows SMS API. Twilio alternative with Australian pricing." />
        <meta property="og:image" content="https://dataflows.com.au/images/integrations/supabase/og-image.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Supabase SMS Integration — Send OTP from Edge Functions" />
        <meta name="twitter:description" content="Add phone authentication to your Supabase app. Open-source Edge Function for OTP delivery via DataFlows SMS API." />
        <link rel="canonical" href={getCanonicalUrl('/integrations/supabase')} />

        {/* Structured data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "DataFlows Supabase SMS Integration",
            "applicationCategory": "DeveloperApplication",
            "operatingSystem": "Cloud",
            "description": "Open-source Edge Function for sending SMS OTP and phone authentication in Supabase applications",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "AUD"
            },
            "author": {
              "@type": "Organization",
              "name": "DataFlows"
            }
          })}
        </script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "Supabase", "item": "https://dataflows.com.au/integrations/supabase" }
          ]
        })}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="supabase-integration-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <IntegrationHero {...heroContent} />

      {/* What is Section */}
      <section className="supabase-overview" data-aos="fade-up">
        <div className="container">
          <div className="overview-content">
            <div className="overview-text">
              <h2 className="section-title">What is Supabase SMS Hook?</h2>
              <p className="section-description left-align">
                The <strong>DataFlows Supabase SMS Hook</strong> is an open-source Edge Function that enables SMS-based authentication for your Supabase project. It intercepts phone authentication requests from Supabase Auth and delivers OTP codes via the DataFlows SMS API.
              </p>
              <p className="section-description left-align">
                Unlike traditional SMS providers that require complex setup and expensive pricing tiers, this integration deploys in minutes and costs a fraction of alternatives like Twilio. The entire codebase is available on GitHub under the MIT license, giving you full transparency and customization control.
              </p>
              <div className="overview-badges">
                <span className="badge">Open Source</span>
                <span className="badge">MIT License</span>
                <span className="badge">Edge Functions</span>
                <span className="badge">TypeScript</span>
              </div>
            </div>
            <div className="overview-graphic" data-aos="fade-left">
              <div className="flow-diagram">
                <div className="flow-step">
                  <div className="flow-icon">👤</div>
                  <div className="flow-label">User requests OTP</div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="flow-icon">⚡</div>
                  <div className="flow-label">Supabase Auth</div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="flow-icon">🔗</div>
                  <div className="flow-label">Edge Function</div>
                </div>
                <div className="flow-arrow">→</div>
                <div className="flow-step">
                  <div className="flow-icon">📱</div>
                  <div className="flow-label">SMS Delivered</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="supabase-features" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Features & Capabilities</h2>
            <p className="section-description">
              Everything you need to implement SMS authentication in your Supabase application
            </p>
          </div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="supabase-use-cases" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Use Cases for Supabase SMS</h2>
            <p className="section-description">
              Common scenarios where SMS authentication enhances your Supabase application
            </p>
          </div>

          <div className="use-cases-grid">
            {useCases.map((useCase, index) => (
              <div key={index} className="use-case-card" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="use-case-icon">{useCase.icon}</div>
                <h3 className="use-case-title">{useCase.title}</h3>
                <p className="use-case-description">{useCase.description}</p>
                <div className="use-case-keywords">
                  {useCase.keywords.map((keyword, i) => (
                    <span key={i} className="keyword-tag">{keyword}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Guide */}
      <section className="supabase-setup" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Step-by-Step Setup Guide</h2>
            <p className="section-description">
              Deploy the Supabase SMS Hook to your project in 6 simple steps
            </p>
          </div>

          <div className="setup-steps">
            {setupSteps.map((step, index) => (
              <div key={step.step} className="setup-step" data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="step-header">
                  <div className="step-number">{step.step}</div>
                  <div className="step-content">
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-description">{step.description}</p>
                  </div>
                </div>
                {step.code && (
                  <div className="step-code">
                    <pre><code>{step.code}</code></pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="setup-cta">
            <a
              href="https://github.com/dataflows-au/supabase-sms-hook"
              target="_blank"
              rel="noreferrer noopener"
              className="cta-button primary"
              onClick={() => trackEvent('supabase_github_clicked', { source: 'setup-guide' })}
            >
              <svg className="github-icon" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
              </svg>
              View Full Setup on GitHub
            </a>
          </div>
        </div>
      </section>

      {/* Code Examples */}
      <section className="supabase-code-examples" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Code Examples</h2>
            <p className="section-description">
              Integrate SMS authentication into your Supabase client application
            </p>
          </div>

          <div className="code-examples-grid">
            <div className="code-example" data-aos="fade-right">
              <h3 className="code-example-title">Request OTP</h3>
              <p className="code-example-description">Send a one-time password to the user's phone number</p>
              <div className="code-block">
                <pre><code>{codeExamples.requestOtp}</code></pre>
              </div>
            </div>

            <div className="code-example" data-aos="fade-left">
              <h3 className="code-example-title">Verify OTP</h3>
              <p className="code-example-description">Validate the code entered by the user</p>
              <div className="code-block">
                <pre><code>{codeExamples.verifyOtp}</code></pre>
              </div>
            </div>
          </div>

          <div className="code-example full-width" data-aos="fade-up">
            <h3 className="code-example-title">Custom Message Template</h3>
            <p className="code-example-description">Personalize your OTP messages with custom branding</p>
            <div className="code-block">
              <pre><code>{codeExamples.customTemplate}</code></pre>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="supabase-comparison" data-aos="fade-up">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">DataFlows vs Twilio for Supabase</h2>
            <p className="section-description">
              Why developers choose DataFlows as their Supabase SMS provider
            </p>
          </div>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="highlight">DataFlows</th>
                  <th>Twilio</th>
                  <th>Savings</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index}>
                    <td>{row.feature}</td>
                    <td className="highlight">{row.dataflows}</td>
                    <td>{row.twilio}</td>
                    <td className="savings">{row.savings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="supabase-benefits" data-aos="fade-up">
        <div className="container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2 className="section-title">Why Use This Integration?</h2>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <li key={index} className="benefit-item" data-aos="fade-up" data-aos-delay={index * 100}>
                    <svg className="benefit-check" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="12" fill="#3ECF8E" fillOpacity="0.1"/>
                      <path d="M17.707 8.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-3-3a1 1 0 111.414-1.414L10 14.586l6.293-6.293a1 1 0 011.414 0z" fill="#3ECF8E"/>
                    </svg>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
            <div className="benefits-specs" data-aos="fade-left">
              <h3 className="specs-title">Technical Specifications</h3>
              <div className="specs-grid">
                {technicalSpecs.map((spec, index) => (
                  <div key={index} className="spec-item">
                    <span className="spec-label">{spec.label}</span>
                    <span className="spec-value">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="supabase-pricing" data-aos="fade-up">
        <div className="container">
          <div className="pricing-content">
            <h2 className="section-title">Simple, Transparent Pricing</h2>
            <p className="pricing-subtitle">The Edge Function is free. You only pay for SMS sent.</p>
            <div className="pricing-grid">
              <div className="pricing-item">
                <div className="price">Free</div>
                <div className="price-label">Edge Function</div>
                <div className="price-note">Open-source, MIT license</div>
              </div>
              <div className="pricing-item featured">
                <div className="price">3.5¢</div>
                <div className="price-label">Per SMS (Australia)</div>
                <div className="price-note">No monthly fees</div>
              </div>
              <div className="pricing-item">
                <div className="price">Free</div>
                <div className="price-label">Inbound SMS</div>
                <div className="price-note">Replies are free</div>
              </div>
            </div>
            <div className="pricing-cta">
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('signup_cta_clicked', { source: 'supabase-pricing' })}
              >
                Get Free API Key
              </a>
              <a
                href="/contact-us"
                className="cta-button secondary"
                onClick={() => trackEvent('contact_support_clicked', { integration: 'supabase' })}
              >
                Enterprise Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="supabase-faq" data-aos="fade-up">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqItems.map((item, index) => (
              <div key={index} className="faq-item" data-aos="fade-up" data-aos-delay={index * 50}>
                <h3 className="faq-question">{item.question}</h3>
                <p className="faq-answer">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Keywords Section (for SEO) */}
      <section className="supabase-keywords" data-aos="fade-up">
        <div className="container">
          <h2 className="section-title">Related Topics</h2>
          <div className="keywords-cloud">
            <Link to="/integrations/zapier" className="keyword-link">Zapier SMS Integration</Link>
            <Link to="/integrations/power-automate" className="keyword-link">Power Automate SMS</Link>
            <span className="keyword-link">Supabase Authentication</span>
            <span className="keyword-link">Phone OTP Verification</span>
            <span className="keyword-link">SMS API Australia</span>
            <span className="keyword-link">Edge Functions</span>
            <span className="keyword-link">Serverless SMS</span>
            <span className="keyword-link">Twilio Alternative</span>
            <span className="keyword-link">Two-Factor Authentication</span>
            <span className="keyword-link">Passwordless Login</span>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="supabase-final-cta">
        <div className="container">
          <div className="cta-content" data-aos="fade-up">
            <h2 className="cta-title">Ready to Add SMS to Your Supabase Project?</h2>
            <p className="cta-description">
              Deploy the open-source Edge Function in minutes and start sending OTP messages to your users today.
            </p>
            <div className="cta-buttons">
              <a
                href="https://github.com/dataflows-au/supabase-sms-hook"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button primary"
                onClick={() => trackEvent('supabase_github_clicked', { source: 'final-cta' })}
              >
                <svg className="github-icon" viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                Star on GitHub
              </a>
              <a
                href="https://sms.dataflows.com.au/register"
                target="_blank"
                rel="noreferrer noopener"
                className="cta-button secondary"
                onClick={() => trackEvent('signup_cta_clicked', { source: 'final-cta' })}
              >
                Get API Key
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      {/* Scroll to Top Button */}
      <div
        className={`scroll-to-top ${showScrollTop ? 'show' : ''}`}
        onClick={scrollToTop}
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/>
        </svg>
      </div>
    </div>
  )
}

export default SupabaseIntegration
